/** Newly opened bookings: today's intake with top lanes, carriers, and owners. */
import type { NewlyOpenedPayload, OpenedGroupEntry } from "@/types/feed";

import {
  carrierBookingsHref,
  customerBookingsHref,
  laneBookingsHref,
  salesPersonBookingsHref,
} from "../../feedLinks";
import { compareScale, formatNum } from "../formatInsight";
import { explainerFor } from "../insightCopy";
import {
  ChangeRow,
  EntitySection,
  InsightShell,
  MetricGrid,
  type MetricItem,
} from "../primitives";

function GroupSection({
  title,
  entries,
  hrefFor,
  party = false,
  lane = false,
}: {
  title: string;
  entries: OpenedGroupEntry[];
  hrefFor?: (label: string) => string | null;
  party?: boolean;
  lane?: boolean;
}) {
  const max = compareScale(entries.map((g) => g.count));
  return (
    <EntitySection title={title}>
      {entries.map((g) => (
        <ChangeRow
          key={g.label}
          name={g.label}
          href={hrefFor ? hrefFor(g.label) : null}
          party={party}
          lane={lane}
          primary={String(g.count)}
          subtitle={g.teu > 0 ? `${formatNum(g.teu)} TEU` : undefined}
          bar={{ type: "share", value: g.count, max }}
        />
      ))}
    </EntitySection>
  );
}

export function NewlyOpenedView({ payload }: { payload: NewlyOpenedPayload }) {
  const m = payload.metrics;
  const metricItems: MetricItem[] = m
    ? [
        { label: "Opened", value: String(m.opened_count) },
        { label: "Opened TEU", value: `${formatNum(m.opened_teu)} TEU` },
        { label: "Prior period", value: String(m.prior_count) },
      ]
    : [];

  return (
    <InsightShell explainer={explainerFor(payload as unknown as Record<string, unknown>)}>
      {metricItems.length > 0 && <MetricGrid items={metricItems} />}
      {payload.top_lanes && payload.top_lanes.length > 0 && (
        <GroupSection
          title="Top lanes"
          entries={payload.top_lanes}
          hrefFor={laneBookingsHref}
          lane
        />
      )}
      {payload.top_carriers && payload.top_carriers.length > 0 && (
        <GroupSection
          title="Top carriers"
          entries={payload.top_carriers}
          hrefFor={carrierBookingsHref}
        />
      )}
      {payload.top_customers && payload.top_customers.length > 0 && (
        <GroupSection
          title="Top customers"
          entries={payload.top_customers}
          hrefFor={customerBookingsHref}
          party
        />
      )}
      {payload.top_sales_persons && payload.top_sales_persons.length > 0 && (
        <GroupSection
          title="By sales person"
          entries={payload.top_sales_persons}
          hrefFor={salesPersonBookingsHref}
          party
        />
      )}
    </InsightShell>
  );
}
